import React, {Fragment} from 'react';
import {FormattedMessage, IntlProvider} from 'react-intl';

import * as ResourceApi from "api/resources";
import IndexedSubSectionWrapper from "hoc/IndexedSubSectionWrapper";
import {createButtonSectionFactoryWithAdditionalArgs} from "../../../../../hoc/CreateButtonSection";
import {FBCommentQuestionToolTip} from 'components/break/tooltips/breaks-steps-section-tool-tips';
import {enUSmessage} from 'i18n/en-US.js';
import {
  UGCCharacterLimitToolTip,
  UGCCustomizedConsentRequiredToolTip,
  UGCCustomizedConsentTextToolTip,
  UGCDefaultAllowDrawingToolTip,
  UGCDefaultAllowPhotoUploadToolTip,
  UGCDefaultAllowTextToolTip,
  UGCExampleImageToolTip,
  UGCFontColorToolTip,
  UGCFontToolTip,
  UGCFrameAllowDrawingToolTip,
  UGCFrameAllowUploadToolTip,
  UGCFrameBackgroundColorToolTip,
  UGCFrameImageToolTip,
  UGCFramesBackgroundColorToolTip,
  UGCFramesButtonTextToolTip,
  UGCFramesHeadlineToolTip,
  UGCFramesIntroToolTip,
  UGCFrameTextColorToolTip,
  UGCGalleryButtonTextToolTip,
  UGCGalleryDescriptionToolTip,
  UGCGalleryHeadlineToolTip,
  UGCGalleryShowSharingToolTip,
  UGCHasStrokeToolTip,
  UGCHeadlineToolTip,
  UGCInstructionDescriptionToolTip,
  UGCInstructionHeadlineToolTip,
  UGCInternalNameToolTip,
  UGCIntroToolTip,
  UGCModerateGalleryToolTip,
  UGCRequestUserEmailToolTip,
  UGCShowCustomizedConsentToolTip,
  UGCShowGalleryToolTip,
  UGCStartButtonTextToolTip,
  UGCSubmissionHeadlineToolTip,
  UGCSubmissionIntroToolTip,
  UGCTextLineHeightToolTip,
  UGCTextPositionToolTip,
  UGCTextThicknessToolTip,
  UGCUserEmailRequiredToolTip,
  UGCUserEmailTextToolTip
} from 'components/break/tooltips/ugc-tool-tips';
import { isNewLayout } from 'utils/break-utils';

export default {
    name: "votableCampaign",
    subSection: true,
    resource: true,
    hideIfResourceIsNoU: true,

    fields: [
        {
            type: 'contenteditable',
            name: 'headline',
            required: true,
            // Headline is only allowed if there is more than one step
            showField: (proxy) => (proxy.currentBaseResource.steps && proxy.currentBaseResource.steps.length > 1),
            linkedField: (formProxy) => ["title"],
            props: {
                labelKey: 'editbreak.steps.ugc_photo.headline',
                helpContent:  <UGCHeadlineToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.headline'}/></IntlProvider>
            }
        },
        {
            type: 'contenteditable',
            name: 'introCopy',
            required: false,
            props: {
              labelKey: 'editbreak.steps.ugc_photo.intro_copy',
              maxLength: 500,
              placeholderKey: 'editbreak.steps.ugc_photo.intro_copy.placeholder',
              helpContent:  <UGCIntroToolTip/>,
              helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.intro_copy'}/></IntlProvider>,
              validationRules: [
                  {
                      type: 'MarkupTextLimit',
                      params: {
                          limit: 500
                      }
                  }
              ]
            }
        },
        {
            type: 'text',
            name: 'commentQuestion',
            showField: (proxy) => (proxy.currentBaseResource.showFBComments && proxy.currentBaseResource.sponsoredType !== 'sponsored' ),
            props: {
                labelKey: 'editbreak.steps.related_breaks.commentQuestion',
                helpContent: <FBCommentQuestionToolTip/>,
                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.breaks_steps.fbCommentQuestion'}/></IntlProvider>
            }
        },
        {
          type: 'image',
          name: 'exampleImageId',
          idFor: 'exampleImage',
          required: true,
          props: {
            labelKey: 'editbreak.steps.ugc_photo.example_image',
            imageType: 'ugcPhotoUploadExample',
            helpContent:  <UGCExampleImageToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.example_image'}/></IntlProvider>
          }
        },
        {
          type: 'text',
          name: 'name',
          required: true,
          props: {
            labelKey: 'editbreak.steps.ugc_photo.internal_name',
            helpContent:  <UGCInternalNameToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.internal_name'}/></IntlProvider>
          }
        },
        {
          type: 'text',
          name: 'buttonText',
          required: true,
          props: {
            labelKey: 'editbreak.steps.ugc_photo.start_button_text',
            helpContent:  <UGCStartButtonTextToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.start_button_text'}/></IntlProvider>
          }
        },
        {
          type: 'yesnoradio',
          name: 'requireUserUpload',
          required: true,
          props: {
            labelKey: 'editbreak.steps.ugc_photo.allow_photo_upload',
            helpContent:  <UGCDefaultAllowPhotoUploadToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.allow_photo_upload'}/></IntlProvider>
          }
        },
        {
          type: 'yesnoradio',
          name: 'defaultAllowDrawing',
          required: true,
          props: {
            labelKey: 'editbreak.steps.ugc_photo.default_allow_drawing',
            helpContent:  <UGCDefaultAllowDrawingToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.default_allow_drawing'}/></IntlProvider>
          }
        },
        {
          conditionalSection: true,
          name: 'caption',
          required: true,
          props: {
            labelKey: 'editbreak.steps.ugc_photo.allow_text',
            helpContent:  <UGCDefaultAllowTextToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.allow_text'}/></IntlProvider>
          },
          fields: [
            {
              type: 'font',
              name: 'captionFont',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.text_font',
                helpContent:  <UGCFontToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.text_font'}/></IntlProvider>
              }
            },
            {
              type: 'color',
              name: 'defaultTextColor',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.text_color',
                helpContent:  <UGCFontColorToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.text_color'}/></IntlProvider>
              }
            },
            {
              type: 'number',
              name: 'captionCharLimit',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.char_limit',
                helpContent:  <UGCCharacterLimitToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.char_limit'}/></IntlProvider>,
                min: 5,
                max: 600
              }
            },
            {
              type: 'number',
              name: 'lineHeight',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.text_line_height',
                helpContent:  <UGCTextLineHeightToolTip/>,
              helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.text_line_height'}/></IntlProvider>,
                min: 1,
                max: 10
              }
            },
            {
              type: 'dropdown',
              name: 'fontWeight',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.text_thickness',
                helpContent:  <UGCTextThicknessToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.text_thickness'}/></IntlProvider>,
                options: [
                  {
                    value: '200',
                    textKey: 'editbreak.steps.ugc_photo.text_thickness.thin'
                  },
                  {
                    value: '400',
                    textKey: 'editbreak.steps.ugc_photo.text_thickness.regular'
                  },
                  {
                    value: '500',
                    textKey: 'editbreak.steps.ugc_photo.text_thickness.medium'
                  },
                  {
                    value: '700',
                    textKey: 'editbreak.steps.ugc_photo.text_thickness.bold'
                  }
                ]
              }
            },
            {
              type: 'yesnoradio',
              name: 'hasStroke',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.stroke',
                helpContent:  <UGCHasStrokeToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.stroke'}/></IntlProvider>
              }
            },
            {
              type: 'radio',
              name: 'captionLayout',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.text_position',
                helpContent:  <UGCTextPositionToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.text_position'}/></IntlProvider>,
                options: [
                  {value: 'top-left', textKey: 'editbreak.steps.ugc_photo.text_position.top_left'},
                  {value: 'left', textKey: 'editbreak.steps.ugc_photo.text_position.left'},
                  {value: 'bottom-left', textKey: 'editbreak.steps.ugc_photo.text_position.bottom_left'},
                  {value: 'top-right', textKey: 'editbreak.steps.ugc_photo.text_position.top_right'},
                  {value: 'right', textKey: 'editbreak.steps.ugc_photo.text_position.right'},
                  {value: 'bottom-right', textKey: 'editbreak.steps.ugc_photo.text_position.bottom_right'}
                ]
              }
            }
          ]
        }, // end text/caption conditional
        {
          type: 'yesnoradio',
          name: 'showGallery',
          required: true,
          props: {
            labelKey: 'editbreak.steps.ugc_photo.show_gallery',
            helpContent:  <UGCShowGalleryToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.show_gallery'}/></IntlProvider>
          }
        },
        {
          name: 'hasAddressValidation',
          type: 'yesnoradio',
          required: true,
          props: {
            labelKey: 'Require Physical Address Validation?',
          }
        },
        {
          type: 'hardcodedButton',
          props: {
            getUrl: (proxy) => `/ugc/${proxy.currentResource.id}`,
            relativeUrl: true,
            textKey: 'buttons.view_submissions'
          }
        },
        {
          subSection: true,
          key: "ugcInstructions",
          sectionWrapper: IndexedSubSectionWrapper,
          props: {
            innerHeader: true,
            getInnerHeader: () => <Fragment><FormattedMessage id={`editbreak.steps.ugc_photo.innerHeading`}/><FormattedMessage id={'editbreak.steps.ugc_photo.instructions_header'}/></Fragment>
          },
          fields: [
            {
                name: 'instructions',
                resource: true,
                subSection: true,
                collection: true,
                sectionWrapper: IndexedSubSectionWrapper,
                deletable: true,
                canDelete: (formProxy) => {
                  var ugcCampaign = formProxy.parentCurrentResource;
                  return ugcCampaign.instructions && ugcCampaign.instructions.length > 1;
                },
                sortable: true,
                sortableName: "instructionsOrder",
                toggleSortLabelKey: 'editbreak.steps.ugc_photo.instruction.reorder_toggle',
                resourceType: ResourceApi.Types.StepInstruction,

                props: {
                    headerKey: 'editbreak.steps.ugc_photo.instruction.header',
                    getCustomSectionHeader: (instruction, sectionIndex) => {
                        return   "Instruction " + (sectionIndex + 1) + (instruction.headline ? ": " + instruction.headline : "");
                    }
                },

                footerContent: createButtonSectionFactoryWithAdditionalArgs({
                    instructionListName: "instructions"
                }, ResourceApi.Types.StepInstruction),

                fields: [
                    {
                        name: 'headline',
                        type: 'contenteditable',
                        required: true,
                        props: {
                            labelKey: 'editbreak.steps.ugc_photo.instruction.headline',
                            helpContent:  <UGCInstructionHeadlineToolTip/>,
                            helpHeader: 'editbreak.steps.ugc_photo.instruction.headline'
                        }
                    },
                    {
                      type: 'contenteditable',
                      name: 'desc',
                      required: false,
                      props: {
                        labelKey: 'editbreak.steps.ugc_photo.instruction.description',
                        helpContent:  <UGCInstructionDescriptionToolTip/>,
                        helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.instruction.description'}/></IntlProvider>
                      }
                    },
                    {
                      type: 'text',
                      name: 'label',
                      required: false,
                      props: {
                        labelKey: 'editbreak.steps.ugc_photo.instruction.label',
                        // helpContent:  <UGCInstructionDescriptionToolTip/>,
                        // helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.instruction.description'}/></IntlProvider>
                      }
                    },
                    {
                      type: 'text',
                      name: 'button',
                      required: false,
                      props: {
                        labelKey: 'editbreak.steps.ugc_photo.instruction.button',
                        // helpContent:  <UGCInstructionDescriptionToolTip/>,
                        // helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.instruction.description'}/></IntlProvider>
                      }
                    },
                    {
                        type: 'contenteditable',
                        name: 'legal',
                        required: false,
                        props: {
                          labelKey: 'editbreak.steps.ugc_photo.instruction.legal',
                        //   helpContent:  <UGCInstructionDescriptionToolTip/>,
                        //   helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.instruction.description'}/></IntlProvider>
                        }
                      }
                ]
            }
          ]
        }, // end ugc instructions
        {
          subSection: true,
          key: "ugcFrames",
          sectionWrapper: IndexedSubSectionWrapper,
          props: {
            innerHeader: true,
            getInnerHeader: () => <Fragment><FormattedMessage id={`editbreak.steps.ugc_photo.innerHeading`}/><FormattedMessage id={'editbreak.steps.ugc_photo.frames_header'}/></Fragment>
          },
          fields: [
            {
              type: 'contenteditable',
              name: 'layersHeadline',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.layers_headline',
                helpContent:  <UGCFramesHeadlineToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.layers_headline'}/></IntlProvider>
              }
            },
            {
              type: 'contenteditable',
              name: 'layersDesc',
              required: false,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.layers_desc',
                helpContent:  <UGCFramesIntroToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.layers_desc'}/></IntlProvider>
              }
            },
            {
              type: 'text',
              name: 'layersBtnTxt',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.layers_button_text',
                helpContent:  <UGCFramesButtonTextToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.layers_button_text'}/></IntlProvider>
              }
            },
            {
              type: 'color',
              name: 'bgColor',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.frame_bg_color',
                helpContent:  <UGCFramesBackgroundColorToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.frame_bg_color'}/></IntlProvider>
              }
            },
            {
              conditionalSection: true,
              name: 'requireUserConsent',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.show_user_consent',
                helpContent:  <UGCShowCustomizedConsentToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.show_user_consent'}/></IntlProvider>
              },
              fields: [
                {
                  type: 'yesnoradio',
                  name: 'userConsentRequired',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.customized_consent_required',
                    helpContent:  <UGCCustomizedConsentRequiredToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.customized_consent_required'}/></IntlProvider>
                  }
                },
                {
                  type: 'contenteditable',
                  name: 'consentText',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.consent_text',
                    helpContent:  <UGCCustomizedConsentTextToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.consent_text'}/></IntlProvider>
                  }
                }
              ]
            },  // end require consent
            {
              conditionalSection: true,
              name: 'requestUserEmail',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.request_user_email',
                helpContent:  <UGCRequestUserEmailToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.request_user_email'}/></IntlProvider>
              },
              fields: [
                {
                  type: 'yesnoradio',
                  name: 'userEmailRequired',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.user_email_required',
                    helpContent:  <UGCUserEmailRequiredToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.user_email_required'}/></IntlProvider>
                  }
                },
                {
                  type: 'contenteditable',
                  name: 'requestUserEmailText',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.request_email_text',
                    helpContent:  <UGCUserEmailTextToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.request_email_text'}/></IntlProvider>
                  }
                }
              ]
            },  // end require user email
            {
              name: 'overlayImages',
              resource: true,
              subSection: true,
                collection: true,
              sectionWrapper: IndexedSubSectionWrapper,
              deletable: true,
              deleteAdditionalArgs: (formProxy) => ({imageType: "overlayImages"}),
              canDelete: (formProxy) => {
                var ugcCampaign = formProxy.parentCurrentResource;
                return ugcCampaign.instructions && ugcCampaign.instructions.length > 1;
              },
              sortable: true,
                sortableName: "ugcOverlayImagesOrder",
                toggleSortLabelKey: 'editbreak.steps.ugc_photo.frames.reorder_toggle',
              resourceType: ResourceApi.Types.UGCImageLayer,

              props: {
                  headerKey: 'editbreak.steps.ugc_photo.frame.header',
                  getCustomSectionHeader: (frame, sectionIndex) => {
                      return   "Frame " + (sectionIndex + 1) + (frame.label ? ": " + frame.label : "");
                  }
              },

              footerContent: createButtonSectionFactoryWithAdditionalArgs({imageType:"overlayImages"},ResourceApi.Types.UGCImageLayer),

              fields: [
                {
                  type: 'image',
                  name: 'imageId',
                  idFor: 'image',
                  require: false,
                    // showField: (proxy) => {
                    //     console.log(proxy)
                    //     console.log(isNewLayout(proxy.currentBaseResource))
                    //     return true
                    // } ,
                  //   showField: (proxy) =>  (!proxy.currentResource.useDefaultImage && !isNewLayout(proxy.currentBaseResource)),
                  showField: (proxy) =>  (!isNewLayout(proxy.currentBaseResource)),
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.frame.image',
                    imageType: 'ugcPhotoUploadOption',
                    helpContent:  <UGCFrameImageToolTip/>,
                    helpHeader: 'editbreak.steps.ugc_photo.frame.image'
                  }
                },
                {
                    type: 'image',
                    name: 'imageId',
                    idFor: 'image',
                    require: false,
                      // showField: (proxy) => {
                      //     console.log(proxy)
                      //     console.log(isNewLayout(proxy.currentBaseResource))
                      //     return true
                      // } ,
                      showField: (proxy) =>  (isNewLayout(proxy.currentBaseResource)),
                    props: {
                      labelKey: 'editbreak.steps.ugc_photo.frame.image2020',
                      imageType: 'ugcPhotoUploadOption2020',
                      helpContent:  <UGCFrameImageToolTip/>,
                      helpHeader: 'editbreak.steps.ugc_photo.frame.image'
                    }
                  },
                  {
                  type: 'yesnoradio',
                  name: 'requiresUpload',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.frame.allow_photo_upload',
                    helpContent:  <UGCFrameAllowUploadToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.frame.allow_photo_upload'}/></IntlProvider>
                  }
                },
                {
                  type: 'yesnoradio',
                  name: 'allowDrawing',
                  required: true,
                  linkedField: (proxy) => {
                    var path = proxy.parentPath();
                    path.push("defaultAllowDrawing");
                    return path;
                  },
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.frame.allow_drawing',
                    helpContent:  <UGCFrameAllowDrawingToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.frame.allow_drawing'}/></IntlProvider>
                  }
                },
                {
                  type: 'color',
                  name: 'bgColor',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.frame.bg_color',
                    helpContent:  <UGCFrameBackgroundColorToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.frame.bg_color'}/></IntlProvider>
                  }
                },
                {
                  type: 'color',
                  name: 'textColor',
                  required: true,
                  props: {
                    labelKey: 'editbreak.steps.ugc_photo.frame.text_color',
                    helpContent:  <UGCFrameTextColorToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.frame.text_color'}/></IntlProvider>
                  }
                }
              ]
            } // end overlay images
          ]
        }, // end frames accordion
        {
          subSection: true,
          key: "ugcSubmissionShare",
          sectionWrapper: IndexedSubSectionWrapper,
          props: {
            innerHeader: true,
            getInnerHeader: () => <Fragment><FormattedMessage id={`editbreak.steps.ugc_photo.innerHeading`}/><FormattedMessage id={'editbreak.steps.ugc_photo.submission_share_header'}/></Fragment>
          },
          fields: [
            {
              type: 'contenteditable',
              name: 'shareHeadline',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.share_headline',
                helpContent:  <UGCSubmissionHeadlineToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.share_headline'}/></IntlProvider>
              }
            },
            {
              type: 'contenteditable',
              name: 'shareDesc',
              required: false,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.share_description',
                helpContent:  <UGCSubmissionIntroToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.share_description'}/></IntlProvider>
              }
            }
          ]
        }, // end submission accordion
        {
          subSection: true,
          key: 'ugcGallerySettings',
          sectionWrapper: IndexedSubSectionWrapper,
          showSection: (proxy) => (proxy.currentResource.showGallery === true),
          props: {
            innerHeader: 'true',
            getInnerHeader: () => <Fragment><FormattedMessage id={`editbreak.steps.ugc_photo.innerHeading`}/><FormattedMessage id={'editbreak.steps.ugc_photo.gallery.header'}/></Fragment>
          },
          fields: [
            {
              type: 'contenteditable',
              name: 'galleryHeadline',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.gallery.headline',
                helpContent:  <UGCGalleryHeadlineToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.gallery.headline'}/></IntlProvider>
              }
            },
            {
              type: 'contenteditable',
              name: 'galleryDesc',
              required: false,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.gallery.description',
                helpContent:  <UGCGalleryDescriptionToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.gallery.description'}/></IntlProvider>
              }
            },
            {
              type: 'text',
              name: 'galleryBtnTxt',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.gallery.button_text',
                helpContent:  <UGCGalleryButtonTextToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.gallery.button_text'}/></IntlProvider>
              }
            },
            {
              type: 'yesnoradio',
              name: 'showGallerySharing',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.gallery.show_sharing',
                helpContent:  <UGCGalleryShowSharingToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.gallery.show_sharing'}/></IntlProvider>
              }
            },
            {
              type: 'yesnoradio',
              name: 'moderateItemsFirst',
              required: true,
              props: {
                labelKey: 'editbreak.steps.ugc_photo.gallery.moderate',
                helpContent:  <UGCModerateGalleryToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.ugc_photo.gallery.moderate'}/></IntlProvider>
              }
            }
          ]
        } // end gallery
    ]
}



// WEBPACK FOOTER //
// ./public/components/EditContent/configs/breaks/steps/ugc.js